import { ComponentProps, FC } from 'react'
import { styled } from '../../../lib/stitches'
import {
  PauseIcon as RawPauseIcon,
  PlayIcon as RawPlayIcon,
  RecordIcon,
  RepeatIcon,
  StopIcon
} from '../../core/icons'
import { blue5, gray1, gray15, red4 } from '../../platform/color'
import { appear } from '../animations'

// STYLED COMPONENTS //

const StyledPlaybackButton = styled('button', {
  all: 'unset',
  width: '26px',
  height: '26px',
  borderRadius: '9999px',
  background: gray15,
  display: 'flex',
  justifyContent: 'center',
  boxSizing: 'border-box',
  alignItems: 'center',
  color: gray1,
  animation: `${appear} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  transition: 'all 150ms ease',
  '&:hover': {
    opacity: '.9',
    transform: 'scale(1.05)'
  },
  '&:active': {
    opacity: '1',
    transform: 'scale(.95)'
  },
  '&:disabled': {
    opacity: '.4'
  },
  variants: {
    big: {
      true: {
        width: '38px',
        height: '38px'
      }
    },
    active: {
      true: {
        background: blue5
      }
    },
    alt: {
      true: {
        color: '$black',
        background: '$gray01',
        boxShadow: '0px 3px 8px 0px #00000026',
        '&:hover': {
          opacity: 1
        },
        '&:active': {
          opacity: 1
        }
      }
    }
  },
  compoundVariants: [
    {
      alt: true,
      active: true,
      css: {
        color: '$gray01',
        boxShadow: 'none'
      }
    }
  ]
})

const StyledPlaybackContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const StyledPlayIcon = styled(RawPlayIcon, {
  width: '16px',
  height: '16px'
})

const StyledPauseIcon = styled(RawPauseIcon, {
  width: '16px',
  height: '16px'
})

const StyledRecordIcon = styled(RecordIcon, {
  width: '12px',
  height: '12px'
})

// MAPPING //

const PlayIcon = StyledPlayIcon
const PauseIcon = StyledPauseIcon
const PlaybackContainer = StyledPlaybackContainer
const PlaybackButton = StyledPlaybackButton
const StudioRecordIcon = StyledRecordIcon

// BUSINESS LOGIC //

type PlayPauseButtonProps = ComponentProps<typeof PlaybackButton> & { isPlaying: boolean }
const PlayPauseButton: FC<PlayPauseButtonProps> = ({ isPlaying, ...props }) => {
  const icon = isPlaying ? <PauseIcon /> : <PlayIcon />
  return (
    <PlaybackButton big css={{ marginLeft: '8px', marginRight: '8px' }} {...props}>
      {icon}
    </PlaybackButton>
  )
}

type RepeatButtonProps = ComponentProps<typeof PlaybackButton> & { isRepeat: boolean }
const RepeatButton: FC<RepeatButtonProps> = ({ isRepeat, ...props }) => (
  <PlaybackButton active={isRepeat} {...props}>
    <RepeatIcon />
  </PlaybackButton>
)

type StopButtonProps = ComponentProps<typeof PlaybackButton>
const StopButton: FC<StopButtonProps> = (props) => (
  <PlaybackButton {...props}>
    <StopIcon />
  </PlaybackButton>
)

type RecordButtonProps = ComponentProps<typeof PlaybackButton> & { isRecording: boolean }
const RecordButton: FC<RecordButtonProps> = (props) => (
  <PlaybackButton {...props} css={{ color: props.isRecording && red4 }}>
    <StudioRecordIcon />
  </PlaybackButton>
)

interface IPlaybackControlsProps {
  isPlaying: boolean
  isRepeat: boolean
  onPlayPause: () => void
  onRepeat: () => void
  onStop: () => void
  disabled?: boolean
}

type PlaybackControlsProps = IPlaybackControlsProps & ComponentProps<typeof PlaybackContainer>

// FINAL COMPOSITION //

const PlaybackControls: FC<PlaybackControlsProps> = (props) => {
  const { isPlaying, isRepeat, onPlayPause, onRepeat, onStop, disabled, ...other } = props
  return (
    <PlaybackContainer {...other}>
      <StopButton
        onClick={() => {
          onStop()
        }}
        disabled={disabled}
      />
      <PlayPauseButton
        onClick={() => {
          onPlayPause()
        }}
        isPlaying={isPlaying}
        disabled={disabled}
      />
      <RepeatButton
        isRepeat={isRepeat}
        onClick={() => {
          onRepeat()
        }}
        disabled={disabled}
      />
    </PlaybackContainer>
  )
}

export const AlternativePlaybackControls: FC<PlaybackControlsProps> = (props) => {
  const { isPlaying, isRepeat, onPlayPause, onRepeat, onStop, disabled, ...other } = props
  return (
    <PlaybackContainer {...other}>
      <StopButton
        alt
        onClick={() => {
          onStop()
        }}
        disabled={disabled}
      />
      <PlayPauseButton
        alt
        onClick={() => {
          onPlayPause()
        }}
        isPlaying={isPlaying}
        disabled={disabled}
      />
      <RepeatButton
        alt
        isRepeat={isRepeat}
        onClick={() => {
          onRepeat()
        }}
        disabled={disabled}
      />
    </PlaybackContainer>
  )
}

interface IStudioHeaderPlaybackControls {
  isPlaying: boolean
  isRepeat: boolean
  isRecording: boolean
  onPlayPause: () => void
  onRecord: () => void
  onStop: () => void
  disabled?: boolean
}

type StudioHeaderPlaybackControls = IStudioHeaderPlaybackControls &
  ComponentProps<typeof PlaybackContainer>

export const StudioHeaderPlaybackControls: FC<StudioHeaderPlaybackControls> = (props) => {
  const { isPlaying, isRepeat, onPlayPause, onRecord, isRecording, onStop, disabled, ...other } =
    props
  return (
    <PlaybackContainer {...other}>
      <StopButton onClick={onStop} disabled={disabled} />
      <PlayPauseButton onClick={onPlayPause} isPlaying={isPlaying} disabled={disabled} />
      <RecordButton onClick={onRecord} isRecording={isRecording} disabled={disabled} />
    </PlaybackContainer>
  )
}

export default PlaybackControls
